import { Button, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { parseISO } from "date-fns";
import {
	ArrowLeftRight,
	Calculator,
	FilePen,
	FileSearch,
	Info,
	type LucideIcon,
	MessageCircle,
	RefreshCw,
	TriangleAlert,
	X,
} from "lucide-react";
import { type FC, useMemo } from "react";
import { useSwipeable } from "react-swipeable";
import {
	AlertBoxActionEnum,
	AlertBoxDismissModeEnum,
	type PortfolioAlertBoxDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getActionSubtext } from "web_myplann/NotificationCenter/EventAlert/actionSubtext";
import { EventActionButton } from "web_myplann/NotificationCenter/EventAlert/EventActionButton";
import { getEventVariant } from "web_myplann/NotificationCenter/EventAlert/eventClassification";
import {
	getDaysUntilEvent,
	getEventTense,
} from "web_myplann/NotificationCenter/EventAlert/eventTiming";
import { formatAlertDate } from "web_myplann/NotificationCenter/EventAlert/formatAlertDate";
import { parseAlertActionStates } from "web_myplann/NotificationCenter/EventAlert/parseAlertActionStates";
import { resolveOfferedActions } from "web_myplann/NotificationCenter/EventAlert/refinancingAvailability";
import type { EventClassificationEnum } from "web_myplann/NotificationCenter/EventAlert/types";

const actionIcon: Record<AlertBoxActionEnum, LucideIcon> = {
	[AlertBoxActionEnum.TalkToAgent]: MessageCircle,
	[AlertBoxActionEnum.CompareRates]: ArrowLeftRight,
	[AlertBoxActionEnum.CheckConditions]: FileSearch,
	[AlertBoxActionEnum.RenewInsurance]: RefreshCw,
	[AlertBoxActionEnum.RecalculateInsurance]: Calculator,
	[AlertBoxActionEnum.SignNewTravelInsurance]: FilePen,
};

const actionLabel = {
	[AlertBoxActionEnum.TalkToAgent]: Resources.EventAlert.Actions.contactAdvisor,
	[AlertBoxActionEnum.CompareRates]: Resources.EventAlert.Actions.compareRates,
	[AlertBoxActionEnum.CheckConditions]:
		Resources.EventAlert.Actions.checkConditions,
	[AlertBoxActionEnum.RenewInsurance]:
		Resources.EventAlert.Actions.renewInsurance,
	[AlertBoxActionEnum.RecalculateInsurance]:
		Resources.EventAlert.Actions.recalculateInsurance,
	[AlertBoxActionEnum.SignNewTravelInsurance]:
		Resources.EventAlert.Actions.arrangeNew,
};

type Props = {
	alert: PortfolioAlertBoxDTO;
	classification: EventClassificationEnum;
	/** Contract `endAt` — decides whether "Porovnat sazby" is still worth offering. */
	contractEndAt?: string | null;
	onAction: (action: AlertBoxActionEnum) => void;
	onDismiss: (mode: AlertBoxDismissModeEnum) => void;
};

export const EventAlertBox: FC<Props> = ({
	alert,
	classification,
	contractEndAt,
	onAction,
	onDismiss,
}) => {
	const { palette } = useTheme();
	const { t } = useResource();

	const { primaryAction, secondaryAction } = resolveOfferedActions(
		alert,
		contractEndAt,
	);
	const content = Resources.EventAlert.Content[classification];
	const variant = getEventVariant(alert.eventType);
	const actionStates = useMemo(() => parseAlertActionStates(alert), [alert]);

	const today = new Date();
	const tense = getEventTense(alert.eventDate, today);
	const days = getDaysUntilEvent(alert.eventDate, today);

	const interpolation = {
		count: days,
		days: t(Resources.EventAlert.inDays, { count: days }),
		daysCap: t(Resources.EventAlert.inDaysCapitalized, { count: days }),
		date: formatAlertDate(parseISO(alert.eventDate)),
	};

	const titleProxy = {
		future: content.titleFuture,
		today: content.titleToday,
		past: content.titlePast,
	}[tense];
	const textProxy = {
		future: content.textFuture,
		today: content.textToday,
		past: content.textPast,
	}[tense];

	const accent = variant === "warning" ? palette.orange : palette.blue;
	const VariantIcon = variant === "warning" ? TriangleAlert : Info;

	const swipeHandlers = useSwipeable({
		onSwipedLeft: () => onDismiss(AlertBoxDismissModeEnum.Temporary),
		onSwipedRight: () => onDismiss(AlertBoxDismissModeEnum.Temporary),
		trackMouse: false,
	});

	return (
		<Stack
			{...swipeHandlers}
			position="relative"
			borderRadius={2}
			bgcolor={accent[50]}
			border={`1px solid ${accent.light}`}
			p={1.6}
			gap={1.2}
		>
			<IconButton
				size="small"
				onClick={() => onDismiss(AlertBoxDismissModeEnum.Temporary)}
				aria-label={t(Resources.EventAlert.dismissAriaLabel)}
				sx={{ position: "absolute", top: 4, right: 4 }}
			>
				<X size={16} color={palette.grey[600]} />
			</IconButton>

			<Stack direction="row" gap={1.2} pr={2.4}>
				<VariantIcon
					size={20}
					color={accent.main}
					style={{ flexShrink: 0, marginTop: 2 }}
				/>
				<Stack gap={0.4} flex={1} sx={{ minWidth: 0 }}>
					<Typography fontSize={15} fontWeight={600} color={accent.main}>
						{t(titleProxy, interpolation)}
					</Typography>
					<Typography variant="body1" color={palette.grey[700]}>
						{t(textProxy, interpolation)}
					</Typography>
				</Stack>
			</Stack>

			<Stack gap={0.4} pt={0.4}>
				{primaryAction && (
					<EventActionButton
						label={t(actionLabel[primaryAction])}
						onClick={() => onAction(primaryAction)}
						variant="contained"
						accent={accent}
						Icon={actionIcon[primaryAction]}
						subtext={getActionSubtext(actionStates[primaryAction], t)}
					/>
				)}
				{secondaryAction && (
					<EventActionButton
						label={t(actionLabel[secondaryAction])}
						onClick={() => onAction(secondaryAction)}
						variant="text"
						accent={accent}
						subtext={getActionSubtext(actionStates[secondaryAction], t)}
					/>
				)}
			</Stack>

			<Button
				variant="text"
				size="small"
				onClick={() => onDismiss(AlertBoxDismissModeEnum.Permanent)}
				sx={{ alignSelf: "center", color: palette.grey[600], px: 0 }}
			>
				{t(Resources.EventAlert.dontNotify)}
			</Button>
		</Stack>
	);
};
